import React, { useEffect, useMemo, useState } from 'react';
import { LINE_TYPE_LABELS, type PnidInstrument, type PnidWizardState, type TextEnrichmentFact } from '../types';
import { getInstrumentEffectiveContext } from '../utils/unifiedInstruments';

export type NoteTargetKind = 'equipment' | 'instrument' | 'line';

export interface NoteTarget {
  kind: NoteTargetKind;
  id: string;
  tag: string;
  label: string;
  context: string;
  note: string;
  status: string;
}

interface DrawingContextPanelProps {
  pnidState: PnidWizardState | null;
  readOnly: boolean;
  onNavigate: (target: NoteTarget) => void;
  onSaveNote: (target: NoteTarget, note: string) => Promise<void>;
}

type PanelTab = 'notes' | 'revisions' | 'objects';
type TargetFilter = 'all' | NoteTargetKind;

const COLORS = {
  text: '#0f172a',
  muted: '#64748b',
  border: '#e2e8f0',
  subtle: '#f8fafc',
  blue: '#2563eb',
  blueSoft: '#eff6ff',
  green: '#15803d',
  greenSoft: '#f0fdf4',
  amber: '#a16207',
  amberSoft: '#fffbeb',
  red: '#b91c1c',
  redSoft: '#fef2f2',
};

const targetLabels: Record<NoteTargetKind, string> = {
  equipment: 'Equipment',
  instrument: 'Instrument',
  line: 'Line',
};

const factValue = (fact: TextEnrichmentFact): string => {
  const raw = fact.override?.raw ?? fact.value?.raw;
  const unit = fact.override?.unit ?? fact.value?.unit;
  if (raw === null || raw === undefined || raw === '') return '—';
  return unit ? `${raw} ${unit}` : raw;
};

const reviewStyle = (status: string) => {
  if (status === 'approved') return { color: COLORS.green, background: COLORS.greenSoft };
  if (status === 'rejected') return { color: COLORS.red, background: COLORS.redSoft };
  return { color: COLORS.amber, background: COLORS.amberSoft };
};

const ReviewBadge = ({ status }: { status: string }) => {
  const style = reviewStyle(status);
  return (
    <span style={{
      color: style.color,
      backgroundColor: style.background,
      borderRadius: 999,
      padding: '2px 7px',
      fontSize: 9,
      fontWeight: 700,
      textTransform: 'capitalize',
      whiteSpace: 'nowrap',
    }}>
      {status}
    </span>
  );
};

const instrumentTag = (instrument: PnidInstrument) =>
  instrument.instrument_text?.trim() || instrument.generated_id?.trim() || `Instrument ${instrument.id.slice(0, 8)}`;

export const buildNoteTargets = (state: PnidWizardState | null): NoteTarget[] => {
  if (!state) return [];

  const equipments = state.equipments.map<NoteTarget>((equipment) => ({
    kind: 'equipment',
    id: equipment.id,
    tag: equipment.equipment_no || `Equipment ${equipment.id.slice(0, 8)}`,
    label: equipment.description || equipment.equipment_type || 'Equipment',
    context: equipment.equipment_type || 'Equipment',
    note: equipment.notes || '',
    status: equipment.status,
  }));

  const lines = state.nodes.map<NoteTarget>((node) => ({
    kind: 'line',
    id: node.id,
    tag: node.line_no || `Line ${node.node_no ?? node.id.slice(0, 8)}`,
    label: [node.source_ref, node.dest_ref].filter(Boolean).join(' → ') || 'Process line',
    context: LINE_TYPE_LABELS[node.line_type ?? 'process'],
    note: node.notes || '',
    status: node.status,
  }));

  const instruments = state.instruments.map<NoteTarget>((instrument) => {
    const effectiveContext = getInstrumentEffectiveContext(instrument, state);
    let context = 'Unassigned instrument';
    if (effectiveContext.context_type === 'equipment') {
      const equipment = state.equipments.find((item) => item.id === effectiveContext.context_id);
      context = `On ${equipment?.equipment_no || 'equipment'}`;
    } else if (effectiveContext.context_type === 'node') {
      const node = state.nodes.find((item) => item.id === effectiveContext.context_id);
      context = `On ${node?.line_no || 'line'}`;
    }
    return {
      kind: 'instrument',
      id: instrument.id,
      tag: instrumentTag(instrument),
      label: [instrument.instrument_type, instrument.subtype].filter(Boolean).join(' / ') || 'Instrument',
      context,
      note: instrument.notes || '',
      status: instrument.status,
    };
  });

  return [...equipments, ...instruments, ...lines].sort((a, b) =>
    a.tag.localeCompare(b.tag, undefined, { numeric: true, sensitivity: 'base' }),
  );
};

const EmptyEnrichment = () => (
  <div style={{
    border: `1px dashed ${COLORS.border}`,
    borderRadius: 10,
    padding: 18,
    color: COLORS.muted,
    backgroundColor: COLORS.subtle,
    fontSize: 12,
    lineHeight: 1.5,
  }}>
    Text enrichment has not produced review data for this drawing yet. Run the full P&amp;ID pipeline or the text-enrichment replay to populate notes and revision metadata.
  </div>
);

const DrawingContextPanel: React.FC<DrawingContextPanelProps> = ({
  pnidState,
  readOnly,
  onNavigate,
  onSaveNote,
}) => {
  const [tab, setTab] = useState<PanelTab>('notes');
  const [filter, setFilter] = useState<TargetFilter>('all');
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const enrichment = pnidState?.text_enrichment ?? null;
  const targets = useMemo(() => buildNoteTargets(pnidState), [pnidState]);
  const selectedTarget = targets.find((target) => `${target.kind}:${target.id}` === selectedKey) ?? null;

  useEffect(() => {
    if (!selectedTarget) return;
    setNoteDraft(selectedTarget.note);
    setSaveError(null);
  }, [selectedTarget?.id, selectedTarget?.kind, selectedTarget?.note]);

  const filteredTargets = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return targets.filter((target) => {
      if (filter !== 'all' && target.kind !== filter) return false;
      if (!normalized) return true;
      return [target.tag, target.label, target.context, target.note]
        .some((value) => value.toLocaleLowerCase().includes(normalized));
    });
  }, [filter, query, targets]);

  const metadata = Object.values(enrichment?.doc_meta ?? {}).sort((a, b) =>
    a.source_label.localeCompare(b.source_label),
  );
  const drawingNotes = Object.values(enrichment?.notes_by_id ?? {}).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
  const revisions = enrichment?.revisions ?? [];
  const targetCounts = targets.reduce<Record<NoteTargetKind, number>>(
    (counts, target) => ({ ...counts, [target.kind]: counts[target.kind] + 1 }),
    { equipment: 0, instrument: 0, line: 0 },
  );

  const noteAttachments = useMemo(() => {
    const attached = new Map<string, string[]>();
    for (const equipment of pnidState?.equipments ?? []) {
      for (const ref of equipment.drawing_note_refs ?? []) {
        if (ref.status === 'rejected') continue;
        const current = attached.get(ref.note_id) ?? [];
        current.push(equipment.equipment_no || equipment.id);
        attached.set(ref.note_id, current);
      }
    }
    return attached;
  }, [pnidState]);

  const saveSelectedNote = async () => {
    if (!selectedTarget || readOnly || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveNote(selectedTarget, noteDraft.trim());
    } catch (error) {
      console.error('Could not save object note', error);
      setSaveError('Could not save this note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabs: Array<{ id: PanelTab; label: string; count?: number }> = [
    { id: 'notes', label: 'Drawing notes', count: drawingNotes.length },
    { id: 'revisions', label: 'Revisions', count: revisions.length },
    { id: 'objects', label: 'Attach to object', count: targets.length },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: '100%' }}>
      <div style={{
        padding: 12,
        borderRadius: 10,
        backgroundColor: COLORS.blueSoft,
        border: '1px solid #bfdbfe',
      }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#1e40af', marginBottom: 3 }}>
          Drawing context
        </div>
        <div style={{ fontSize: 11, color: '#1e3a8a', lineHeight: 1.45 }}>
          Review what the drawing says, then attach SME notes to a specific equipment, instrument, or line from the dataset below.
        </div>
      </div>

      <div role="tablist" aria-label="Drawing context sections" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 4,
        padding: 4,
        backgroundColor: COLORS.subtle,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 9,
      }}>
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            style={{
              minWidth: 0,
              border: tab === item.id ? `1px solid ${COLORS.border}` : '1px solid transparent',
              borderRadius: 6,
              backgroundColor: tab === item.id ? 'white' : 'transparent',
              color: tab === item.id ? COLORS.text : COLORS.muted,
              padding: '7px 5px',
              fontSize: 10,
              lineHeight: 1.2,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: tab === item.id ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
            }}
          >
            {item.label}{typeof item.count === 'number' ? ` (${item.count})` : ''}
          </button>
        ))}
      </div>

      {tab === 'notes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!enrichment ? <EmptyEnrichment /> : (
            <>
              <section>
                <h4 style={{ margin: '0 0 8px', fontSize: 12, color: COLORS.text }}>Document metadata</h4>
                {metadata.length === 0 ? (
                  <div style={{ fontSize: 11, color: COLORS.muted }}>No document metadata was extracted.</div>
                ) : (
                  <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden' }}>
                    {metadata.map((fact, index) => (
                      <div key={fact.key} style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(110px, 0.8fr) minmax(0, 1.2fr) auto',
                        gap: 8,
                        alignItems: 'center',
                        padding: '8px 10px',
                        borderTop: index ? `1px solid ${COLORS.border}` : 'none',
                        backgroundColor: index % 2 ? COLORS.subtle : 'white',
                      }}>
                        <span style={{ fontSize: 10, color: COLORS.muted }}>{fact.source_label || fact.key}</span>
                        <strong style={{ fontSize: 11, color: COLORS.text, wordBreak: 'break-word' }}>{factValue(fact)}</strong>
                        <ReviewBadge status={fact.review.status} />
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h4 style={{ margin: '0 0 8px', fontSize: 12, color: COLORS.text }}>Drawing notes</h4>
                {drawingNotes.length === 0 ? (
                  <div style={{ fontSize: 11, color: COLORS.muted }}>No drawing notes were extracted.</div>
                ) : drawingNotes.map((note) => {
                  const attachments = noteAttachments.get(note.id) ?? [];
                  return (
                    <article key={note.id} style={{
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 9,
                      padding: 11,
                      marginBottom: 8,
                      backgroundColor: 'white',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{
                          width: 24,
                          height: 24,
                          borderRadius: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: COLORS.blueSoft,
                          color: COLORS.blue,
                          fontSize: 11,
                          fontWeight: 800,
                        }}>{note.label}</span>
                        <ReviewBadge status={note.review.status} />
                      </div>
                      <div style={{ fontSize: 12, lineHeight: 1.5, color: COLORS.text }}>
                        {note.override_text || note.text}
                      </div>
                      {attachments.length > 0 && (
                        <div style={{ marginTop: 8, fontSize: 10, color: COLORS.muted }}>
                          Suggested for: {attachments.join(', ')}
                        </div>
                      )}
                    </article>
                  );
                })}
              </section>
            </>
          )}
        </div>
      )}

      {tab === 'revisions' && (
        !enrichment ? <EmptyEnrichment /> : revisions.length === 0 ? (
          <div style={{ fontSize: 11, color: COLORS.muted }}>No revision rows were extracted.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {revisions.map((revision) => (
              <article key={revision.id} style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: 9,
                overflow: 'hidden',
                backgroundColor: 'white',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '9px 11px',
                  backgroundColor: COLORS.subtle,
                  borderBottom: `1px solid ${COLORS.border}`,
                }}>
                  <strong style={{ color: COLORS.text, fontSize: 12 }}>Revision {revision.rev || '—'}</strong>
                  <ReviewBadge status={revision.review.status} />
                </div>
                {Object.values(revision.fields).map((fact) => (
                  <div key={fact.key} style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(110px, 0.8fr) minmax(0, 1.2fr)',
                    gap: 10,
                    padding: '7px 11px',
                    borderTop: `1px solid ${COLORS.border}`,
                  }}>
                    <span style={{ color: COLORS.muted, fontSize: 10 }}>{fact.source_label || fact.key}</span>
                    <span style={{ color: COLORS.text, fontSize: 11, fontWeight: 600 }}>{factValue(fact)}</span>
                  </div>
                ))}
              </article>
            ))}
          </div>
        )
      )}

      {tab === 'objects' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search tag, line number, type, or note…"
            aria-label="Search note targets"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: `1px solid ${COLORS.border}`,
              borderRadius: 8,
              padding: '9px 10px',
              fontSize: 11,
              color: COLORS.text,
              outline: 'none',
            }}
          />

          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {(['all', 'equipment', 'instrument', 'line'] as TargetFilter[]).map((kind) => {
              const count = kind === 'all' ? targets.length : targetCounts[kind];
              return (
                <button
                  key={kind}
                  type="button"
                  onClick={() => setFilter(kind)}
                  style={{
                    border: filter === kind ? '1px solid #93c5fd' : `1px solid ${COLORS.border}`,
                    backgroundColor: filter === kind ? COLORS.blueSoft : 'white',
                    color: filter === kind ? '#1d4ed8' : COLORS.muted,
                    borderRadius: 999,
                    padding: '4px 8px',
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {kind === 'all' ? 'All' : targetLabels[kind]} ({count})
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 10, color: COLORS.muted }}>
            {filteredTargets.length} of {targets.length} objects. Select one to add or edit its SME note.
          </div>

          <div style={{
            maxHeight: selectedTarget ? 290 : 520,
            overflowY: 'auto',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 9,
          }}>
            {filteredTargets.length === 0 ? (
              <div style={{ padding: 18, color: COLORS.muted, fontSize: 11 }}>No matching objects.</div>
            ) : filteredTargets.map((target, index) => {
              const key = `${target.kind}:${target.id}`;
              const active = key === selectedKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedKey(key)}
                  style={{
                    width: '100%',
                    border: 'none',
                    borderTop: index ? `1px solid ${COLORS.border}` : 'none',
                    backgroundColor: active ? COLORS.blueSoft : 'white',
                    padding: '9px 10px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <strong style={{
                        color: COLORS.text,
                        fontSize: 11,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>{target.tag}</strong>
                      <span style={{ color: COLORS.muted, fontSize: 9 }}>{targetLabels[target.kind]}</span>
                    </span>
                    <span style={{ display: 'block', color: COLORS.muted, fontSize: 10, marginTop: 2 }}>
                      {target.label} · {target.context}
                    </span>
                  </span>
                  {target.note && (
                    <span style={{
                      color: COLORS.green,
                      backgroundColor: COLORS.greenSoft,
                      borderRadius: 999,
                      padding: '2px 6px',
                      fontSize: 9,
                      fontWeight: 700,
                    }}>Has note</span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedTarget && (
            <div style={{
              border: '1px solid #bfdbfe',
              borderRadius: 10,
              padding: 11,
              backgroundColor: COLORS.blueSoft,
            }}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.text }}>{selectedTarget.tag}</div>
                  <div style={{ fontSize: 10, color: COLORS.muted, marginTop: 2 }}>{selectedTarget.label}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate(selectedTarget)}
                  style={{
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 6,
                    backgroundColor: 'white',
                    color: COLORS.blue,
                    padding: '5px 8px',
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Show on drawing
                </button>
              </div>
              <label style={{ display: 'block', color: COLORS.text, fontSize: 10, fontWeight: 700, marginBottom: 5 }}>
                SME note
              </label>
              <textarea
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                readOnly={readOnly}
                rows={4}
                placeholder="Add context specific to this object…"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 7,
                  padding: 9,
                  backgroundColor: readOnly ? COLORS.subtle : 'white',
                  color: COLORS.text,
                  fontFamily: 'inherit',
                  fontSize: 11,
                  lineHeight: 1.45,
                }}
              />
              {saveError && <div style={{ color: COLORS.red, fontSize: 10, marginTop: 6 }}>{saveError}</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ color: COLORS.muted, fontSize: 9 }}>
                  {readOnly ? 'Reviewer access is read-only.' : 'Saved on this object, separate from canvas comments.'}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={saveSelectedNote}
                    disabled={saving || noteDraft.trim() === selectedTarget.note.trim()}
                    style={{
                      border: 'none',
                      borderRadius: 7,
                      backgroundColor: COLORS.blue,
                      color: 'white',
                      padding: '7px 11px',
                      fontSize: 10,
                      fontWeight: 800,
                      cursor: saving || noteDraft.trim() === selectedTarget.note.trim() ? 'not-allowed' : 'pointer',
                      opacity: saving || noteDraft.trim() === selectedTarget.note.trim() ? 0.55 : 1,
                    }}
                  >
                    {saving ? 'Saving…' : 'Save note'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DrawingContextPanel;
